import {Component, OnDestroy, OnInit} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {DialogService} from 'primeng/dynamicdialog';
import {Partners} from '../../models/PartnersModel';
import {UpgradeInfoResponse, UserModel} from '../../models/User';
import {catchError, map, switchMap, take, takeUntil, tap, timeout, withLatestFrom} from 'rxjs/operators';
import {ApplyCouponResponse, CouponInfo, CouponMeta, CouponResponse, getBucketForId, MembershipBucket, MembershipInfo, ProductNames, Tiers} from '../../models/SubscriptionsModels';
import {SubscriptionService, trialCodeToDays} from '../../services/subscription.service';
import {DEFAULT_PARTNER_ID, PartnerService} from '../../services/partner.service';
import {MixpanelService} from '../../services/mixpanel.service';
import {AuthService} from '../../services/auth.service';
import {CheckoutModalComponent, CheckoutOptions} from '../checkout/checkout.component';
import {UserService} from '../../services/user.service';
import {CarouselResponsiveOptions} from 'primeng/carousel';
import {of} from 'rxjs/internal/observable/of';
import {ToastrService} from 'ngx-toastr';
import {environment} from '../../../environments/environment';
import {DowngradeModalActions, DowngradeModalComponent, DowngradeModalData, DowngradeModalVariant} from '../modals/downgrade-modals/DowngradeModals';
import {Observable} from 'rxjs/internal/Observable';
import {buildMemberships, BuildMembershipsCtx, MembershipPage} from '../../helpers/plans';
import {DEFAULT_FAQS, DEFAULT_FEATURES} from '../../models/CarouselConfigs';
import {combineLatest} from 'rxjs/internal/observable/combineLatest';
import {TrackingParams} from '../../models/Authentication';
import {Subject} from 'rxjs';
import {PLANS_CAROUSEL_BREAKPOINT} from '../../utilities/helpers';
import {ViewportService} from '../../services/viewport.service';
import {ReplaySubject} from 'rxjs/internal/ReplaySubject';
import {filter} from 'rxjs/internal/operators/filter';
import {ChurnkeyService} from '../../services/churnkey.service';
import {PauseDurationMonths, UpdateModalComponent} from '../modals/update-modal/update-modal.component';
import {openModal} from 'src/app/utilities/helpers';

interface UiState {
    isMobile: boolean;
    displayPlanCarousel: boolean;
    displayEmailModal: boolean;
    isAuthenticated: boolean;
    partnerLogoUrl: string;
    bannerText: string;
    cancelAtDate?: string;
    isLoading: boolean;
}

interface Plan {
    name: ProductNames;
    tier: Tiers;
    membership: ReturnType<typeof getBucketForId>;
    isPopular?: boolean;
}

@Component({
    selector: 'app-upgrade',
    templateUrl: './upgrade.component.html',
    styleUrls: ['./upgrade.component.scss'],
    providers: [DialogService]
})
export class UpgradeComponent implements OnInit, OnDestroy {
    private destroy$ = new Subject<void>();
    labelsCtx!: BuildMembershipsCtx;
    protected readonly Tiers = Tiers;

    ui: UiState = {
        //typical mobile breakpoint
        isMobile: false,
        //breakpoint for plans carousel
        displayPlanCarousel: false,
        displayEmailModal: false,
        isAuthenticated: false,
        partnerLogoUrl: '',
        bannerText: '',
        isLoading: false
    };

    tracking: TrackingParams = {};

    coupon: CouponInfo = {
        code: '',
        discount: ''
    };

    partner: Partners | null;
    partnerId: string;
    filteredMembershipsArray = [];
    ProductNames = ProductNames;
    couponMeta: CouponMeta;
    user: UpgradeInfoResponse;
    email: string;
    cancelAtDate: string;
    daysInTrial?: string;
    pageReady$ = new ReplaySubject<boolean>(1);

    responsiveOptions: CarouselResponsiveOptions[] = [
        {breakpoint: '1600px', numVisible: 3, numScroll: 1},
        {breakpoint: '1200px', numVisible: 2.5, numScroll: 1},
        {breakpoint: '949px', numVisible: 2.2, numScroll: 1},
        {breakpoint: '768px', numVisible: 1.3, numScroll: 1},
        {breakpoint: '480px', numVisible: 1, numScroll: 1},
        {breakpoint: '320px', numVisible: 1, numScroll: 1}
    ];

    steps = DEFAULT_FEATURES
    memberships = [];
    faqs = DEFAULT_FAQS
    protected readonly environment = environment;

    constructor(
        private router: Router,
        private dialogService: DialogService,
        private partnerService: PartnerService,
        private mixpanel: MixpanelService,
        private route: ActivatedRoute,
        private subscriptionService: SubscriptionService,
        private auth: AuthService,
        private userService: UserService,
        private toast: ToastrService,
        public viewport: ViewportService,
        private churnkeyService: ChurnkeyService
    ) {
    }

    ngOnInit(): void {
        this.pageReady$.next(false);

        const partner$ = this.route.paramMap.pipe(
            map(pm => pm.get('partnerId') || DEFAULT_PARTNER_ID),
            switchMap(id => {
                this.partnerService.setPartnerId(id);
                return this.partnerService.partner$.pipe(
                    filter((p): p is Partners => !!p),
                    filter(p => String(p.name) === String(id)),
                    take(1),
                    timeout(5000),
                    catchError(() => {
                        if (id !== DEFAULT_PARTNER_ID) {
                            this.router.navigate(['../', DEFAULT_PARTNER_ID], {
                                relativeTo: this.route,
                                replaceUrl: true,
                                queryParamsHandling: 'preserve',
                            });
                        }
                        this.partnerService.setPartnerId(DEFAULT_PARTNER_ID);
                        return this.partnerService.partner$.pipe(
                            filter((p): p is Partners => !!p),
                            filter(p => String(p.name) === String(DEFAULT_PARTNER_ID)),
                            take(1)
                        );
                    })
                );
            }),
            takeUntil(this.destroy$)
        );

        const userAndCoupon$ = this.route.queryParams.pipe(
            take(1),
            tap((params: any) => {
                this.tracking.rfsn = params.rfsn;
                this.tracking.utm_source = params.utm_source;
                this.tracking.utm_medium = params.utm_medium;
                this.tracking.utm_campaign = params.utm_campaign;
                this.tracking.utm_content = params.utm_content;
                this.tracking.utm_term = params.utm_term;
                this.tracking.trafficSource = params.source;
                this.tracking.trialCode = params.code;
                this.tracking.upgrade_send_id = params.upgrade_send_id;
                this.tracking.upgrade_campaign = params.upgrade_campaign;
                this.tracking.upgrade_cohort = params.upgrade_cohort;
                this.tracking.upgrade_origin = params.upgrade_origin;
                this.tracking.upgrade_source = params.upgrade_source;
                this.tracking.location = 'Upgrade Page';
                this.daysInTrial = trialCodeToDays(params.code, {dash: false});
            }),
            switchMap(params => {
                const coupon$ = params.coupon
                    ? this.subscriptionService.checkCoupon(params.coupon)
                        .pipe(catchError(() => of(null)))
                    : of(null);

                const user$ = params.email
                    ? this.auth.getMembershipInfoForUpgradePage(params.email).pipe(catchError(() => of(null)))
                    : this.auth.loadSession().pipe(
                        tap(() => (this.ui.isAuthenticated = true)),
                        catchError(() => of(null))
                    );

                return combineLatest([coupon$, user$]).pipe(take(1));
            }),
            tap(([coupon, user]: [CouponResponse | null, any]) => {
                if (coupon?.valid) {
                    this.coupon.code = coupon.id;
                    this.coupon.discount = coupon.percent_off
                        ? `${coupon.percent_off.toFixed(0)}%`
                        : `$${(coupon.amount_off / 100).toFixed(0)}`;
                    this.couponMeta = {
                        coupon: this.coupon.code,
                        percent_off: coupon.percent_off,
                        amount_off: coupon.amount_off,
                        valid: coupon.valid,
                    };
                }
                if (user) {
                    this.user = {email: user.email, id: user.id, membershipId: user.membershipId};
                    this.setMixpanel(user.id);
                } else {
                    this.ui.displayEmailModal = true;
                }
            }),
            takeUntil(this.destroy$)
        );

        combineLatest([partner$, userAndCoupon$]).pipe(
            takeUntil(this.destroy$)
        ).subscribe(([partner]) => {
            this.partner = partner;
            this.ui.partnerLogoUrl = partner?.logoUrl || '';
            this.ui.bannerText = partner?.bannerText || '';
            this.refreshPlans();
            this.pageReady$.next(true);
        });

        this.viewport.dimensions$.pipe(
            takeUntil(this.destroy$),
            withLatestFrom(this.pageReady$),
            filter(([, ready]) => ready === true)
        ).subscribe(() => this.refreshPlans());
    }

    ngOnDestroy() {
        this.destroy$.next()
        this.destroy$.complete()
    }

    setMixpanel(userId: number){
        this.mixpanel.identify(userId.toString());
        this.mixpanel.track('Upgrade Page Visited', {
            upgrade_source: this.tracking.upgrade_source,
            upgrade_campaign: this.tracking.upgrade_campaign,
            upgrade_cohort: this.tracking.upgrade_cohort,
        });
    }
    submitEmail() {
        if (!this.email) {
            this.toast.error('Enter your email to continue', 'Email Required');
            return;
        }
        this.auth.getMembershipInfoForUpgradePage(this.email).pipe(tap(user => {
                this.user = this.auth.getUser();
                this.setMixpanel(user.id);
                this.updateMemberships();
                this.refreshPlans();
                this.ui.displayEmailModal = false;
            }),
            catchError(err => {
                this.toast.error('Please visit our signup page', 'Account not found');
                return of(null);
            })).subscribe();
    }

    trackByPlanName(index: number, plan: any) {
        return plan.name;
    }
    private ensureCurrentPlanIncluded(plans: Plan[], allPlans: Plan[], userBucket?: ReturnType<typeof getBucketForId>): Plan[] {
        if (!userBucket) {
            return plans;
        }
        const alreadyHas = plans.some(p => p.membership === userBucket);
        if (alreadyHas) {
            return plans;
        }

        const currentPlan = allPlans.find(p => p.membership === userBucket);
        if (!currentPlan) {
            return plans;
        }
        // If Premium Plus is being shown as an upsell
        // AND the user's current plan is Premium,
        // then remove the "Popular" tag from Premium.
        const prem = this.memberships.find(p => p.tier === Tiers.Premium);
        const pplus = plans.some(p => p.tier === Tiers.PremiumPlus);

        if ((currentPlan === prem) && pplus) {
            prem.isPopular = false;
        }

        const withCurrentPlan =  [currentPlan, ...plans];
        return allPlans.filter(p => withCurrentPlan.some(c => c.membership === p.membership));

    }
     updateMemberships() {
        if (!this.partner) {
            return;
        }
        this.memberships = buildMemberships({
            page: MembershipPage.Upgrade,
            isAuthenticated: this.ui.isAuthenticated,
            partner: this.partner,
            onDowngrade: () => {
                this.ui.isLoading = true;
                this.churnkeyService.openCancelFlow(
                    (offerType) => this.openPauseModalFromUpgrade(offerType)
                ).then(() => {
                    this.ui.isLoading = false
                });
            },
            onUpgrade: (tier) => this.upgrade(tier),
            navigateToLogin: () => this.router.navigate(
                ['/login'], { queryParams: { returnUrl: this.router.url } }
            ),
            couponDiscount: this.coupon?.discount || undefined,
            couponAmountOff: this.couponMeta?.amount_off || undefined,
            couponPercentOff: this.couponMeta?.percent_off || undefined,
            currentMembershipBucket: getBucketForId(this.user?.membershipId) ?? MembershipBucket.Free,
            trialCode: this.tracking.trialCode || undefined
        });
    }

    private rebuildLabelsCtx() {
        this.labelsCtx = {
            page: MembershipPage.Upgrade,
            isAuthenticated: this.ui.isAuthenticated,
            partner: this.partner,
            onDowngrade: () => {
                this.ui.isLoading = true;
                this.churnkeyService.openCancelFlow(
                    (offerType) => this.openPauseModalFromUpgrade(offerType)
                ).then(() => {
                    this.ui.isLoading = false
                });
            },
            onUpgrade: (tier) => this.upgrade(tier),
            navigateToLogin: () => this.router.navigate(['/login'], { queryParams: { returnUrl: this.router.url } }),
            couponDiscount: this.coupon?.discount || undefined,
            couponAmountOff: this.couponMeta?.amount_off || undefined,
            couponPercentOff: this.couponMeta?.percent_off || undefined,
            currentMembershipBucket: getBucketForId(this.user?.membershipId) ?? MembershipBucket.Free,
            trialCode: this.tracking.trialCode || undefined
        };
    }

    private openPauseModalFromUpgrade(offerType: PauseDurationMonths): void {
        if (!this.user) {
            this.toast.error("Cannot redeem pause offer at this time, please try again or contact support")
            return;
        }
        openModal(this.dialogService, UpdateModalComponent, {
            styleClass: 'dashboard-modal small-modal',
            data: {
                user: this.user,
                offerType

            }},
        )
    }

    refreshPlans() {
        this.updateMemberships();
        this.rebuildLabelsCtx();

        const raw = this.filteredMembershipsWithoutCarouselSort();

        this.ui.isMobile = this.viewport.isMobile;
        this.ui.displayPlanCarousel = raw.length === 3
            ? this.viewport.dimensions.width < PLANS_CAROUSEL_BREAKPOINT
            : this.viewport.isMobile;

        this.filteredMembershipsArray = this.applyCarouselSort(raw);
    }

    private filteredMembershipsWithoutCarouselSort(): any[] {
        if (!this.partner || !this.memberships.length) {
            return [];
        }

        const { shouldShowPremiumPlusOffer, shouldShowContinueBtn } = this.partner;
        let filtered= [] as typeof this.memberships;

        //Only display Premium & PPlus plans
        if (!shouldShowPremiumPlusOffer && !shouldShowContinueBtn) {
            const plans = this.memberships.filter(p => [Tiers.Premium, Tiers.PremiumPlus].includes(p.tier));
            const prem = plans.find(p => p.tier === Tiers.Premium);
            if (prem) prem.isPopular = false;
            filtered = plans;
            //Only display PPlus plan
        } else if (shouldShowPremiumPlusOffer && !shouldShowContinueBtn) {
            filtered = this.memberships.filter(p => p.tier === Tiers.PremiumPlus);
            //Display PPlus & free
        } else if (shouldShowPremiumPlusOffer && shouldShowContinueBtn) {
            filtered = this.memberships.filter(p => [Tiers.Free, Tiers.PremiumPlus].includes(p.tier));
            //Display all 3, make "isPopular" tag only present on PPlus
        } else {
            const prem = this.memberships.find(p => p.tier === Tiers.Premium);
            if (prem) {
                prem.isPopular = false;
            }
            filtered = [...this.memberships];
        }

        //For mobile carousel view, swap plan order (show highest tier first)
        const userBucket = getBucketForId(this.user?.membershipId);
        filtered = this.ensureCurrentPlanIncluded(filtered, this.memberships, userBucket);

        return filtered.map(plan => ({
            ...plan,
            isCurrentPlan: plan.membership === userBucket
        }));
    }

    private applyCarouselSort(plans: any[]): any[] {
        if (!this.ui.displayPlanCarousel) return plans;
        const order = [Tiers.PremiumPlus, Tiers.Premium, Tiers.Free];
        return [...plans].sort((a, b) => order.indexOf(a.tier) - order.indexOf(b.tier));
    }



    upgrade(plan: string) {
        this.dialogService.open(CheckoutModalComponent, {
            data: {
                plan: plan,
                partner: this.partner,
                onboardingRedirect: false,
                coupon: this.coupon.code,
                couponMeta: this.couponMeta,
                trackingParams: this.tracking
            } satisfies CheckoutOptions,
            width: '400px',
            breakpoints: {
                '500px': '90vw'
            },
            showHeader: false,
            styleClass: 'checkout-dialog p-relative no-scrollbar',
            dismissableMask: true,
            appendTo: document.body,
            contentStyle: {
                borderRadius: '6px',
                padding: '5px',
                scrollbarWidth: 'none'
            }

        });
    }

    toggleAccordion(item: any): void {
        item.open = !item.open;
    }

    openModal(variant: DowngradeModalVariant) {
        this.subscriptionService.getUserCurrentPlanInfo().pipe(
            take(1),
            map((sub: any) => {
                const onTrial = !!sub?.trialActive || sub?.status !== 'active';

                const actions: DowngradeModalActions = {
                    onDowngrade: () => this._downgrade$(),
                    keepPlanWithCoupon: () => {
                        const code = onTrial ? environment.trialCoupon : environment.cancellationCoupon;
                        return this.subscriptionService.applyCoupon(code);
                    },
                    continueFree: () => this.continueFree(),
                };

                const data: DowngradeModalData = {
                    variant,
                    onTrial,
                    actions: variant === DowngradeModalVariant.Done
                        ? { continueFree: () => this.continueFree() }
                        : actions,
                    cancelAtDate: variant === DowngradeModalVariant.Done ? this.cancelAtDate : undefined,
                };

                return data;
            })
        ).subscribe((data: DowngradeModalData) => {
            this.dialogService.open(DowngradeModalComponent, {
                showHeader: true,
                styleClass: 'downgrade-modal dashboard-modal',
                dismissableMask: true,
                contentStyle: { scrollbarWidth: 'none' },
                data
            });
        });
    }


    showBeforeYouDowngradeModal() {
        this.openModal(DowngradeModalVariant.Before);
    }

    continueFree() {
        window.location.href = `${environment.accountUrl}`;
    }

    getBannerStyle() {
        const count = this.filteredMembershipsArray.length;
        if (count === 1) {
            return {
                'max-width.px': 360, width: '100%', marginLeft: 'auto', marginRight: 'auto'
            };
        } else if (count === 2){
            return {
                'max-width.px': 860, width: '100%'
            };
        }
        return {
            'max-width.px': 1120, width: '100%'
        }
    }

    getOneYearFree(): Observable<ApplyCouponResponse> | void {
        return this.subscriptionService.applyCoupon(environment.cancellationCoupon)
    }
    private _downgrade$(): Observable<string> {
        return this.subscriptionService.downgradeToFree(this.user as UserModel).pipe(
            tap(sub => {
                this.cancelAtDate = new Date(sub.endDate * 1000).toLocaleDateString('en-US', {
                    year: 'numeric', month: 'long', day: 'numeric'
                });
            }),
            map(sub =>
                new Date(sub.endDate * 1000).toLocaleDateString('en-US', {
                    year: 'numeric', month: 'long', day: 'numeric'
                })
            )
        );
    }
}
